import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  Typography
} from '@mui/material';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  Tooltip,
  Legend
} from 'recharts';

/**
 * DomainVulnerabilityRadar Component
 * Radar chart showing compliance rates across SCF control domains
 */
export const DomainVulnerabilityRadar = ({ data }) => {
  const { t } = useTranslation(['dashboard']);

  // Handle null/undefined data
  // Backend returns { domains: string[], scores: number[], vendorCount: number[] }
  // OR array of objects [{domain, percentage}] — support both formats
  const isEmpty = !data ||
    (Array.isArray(data) && data.length === 0) ||
    (!Array.isArray(data) && (!data.domains || data.domains.length === 0));

  if (isEmpty) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>{t('dashboard:domainRadar.noDataTitle')}</Typography>
          <Typography color="textSecondary">{t('dashboard:domainRadar.noData')}</Typography>
        </CardContent>
      </Card>
    );
  }

  // Transform data for radar chart — support both formats
  let chartData;
  if (Array.isArray(data)) {
    // Array of objects format
    chartData = data.map(domain => ({
      domain: domain.domain || domain.name,
      score: domain.percentage || domain.score,
      fullMark: 100
    }));
  } else {
    // Parallel arrays format from backend: { domains, scores, vendorCount }
    chartData = data.domains.map((domain, idx) => ({
      domain,
      score: data.scores[idx] || 0,
      fullMark: 100
    }));
  }

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div
          style={{
            backgroundColor: '#fff',
            padding: '12px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
          }}
        >
          <Typography variant="subtitle2" fontWeight="bold">{data.payload.domain}</Typography>
          <Typography variant="body2" color="primary">
            {t('dashboard:domainRadar.complianceRateValue', { value: data.value.toFixed(1) })}
          </Typography>
        </div>
      );
    }
    return null;
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>{t('dashboard:domainRadar.title')}</Typography>
        <Typography variant="body2" color="textSecondary" mb={2}>
          {t('dashboard:domainRadar.subtitle')}
        </Typography>

        <ResponsiveContainer width="100%" height={400}>
          <RadarChart data={chartData}>
            <PolarGrid />
            <PolarAngleAxis dataKey="domain" />
            <PolarRadiusAxis angle={90} domain={[0, 100]} />
            <Radar
              name={t('dashboard:domainRadar.complianceRate')}
              dataKey="score"
              stroke="#8884d8"
              fill="#8884d8"
              fillOpacity={0.6}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
          </RadarChart>
        </ResponsiveContainer>

        <Typography variant="caption" color="textSecondary" display="block" mt={1}>
          {t('dashboard:domainRadar.footer')}
        </Typography>
      </CardContent>
    </Card>
  );
};
